import { useQuery } from '@tanstack/react-query';
import { useMatchRoute, useRouterState } from '@tanstack/react-router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PageContext } from './api-types';
import { schemaQuery } from './queries';
import { getPageHelp } from './page-help';

type PageType = 'activity' | 'daily-brief' | 'object-index' | 'record-detail' | 'help' | 'other';

type PageContextValue = {
  pageContext: PageContext;
  pageType: PageType;
  scopeLabel: string;
  setPageSpecificContext: (context: Record<string, unknown>) => void;
};

const CurrentPageContext = createContext<PageContextValue | null>(null);

export function PageContextProvider({ children }: { children: ReactNode }) {
  const schema = useQuery(schemaQuery());
  const matchRoute = useMatchRoute();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const [registeredContext, setRegisteredContext] = useState<{ path: string; value: Record<string, unknown> }>({ path, value: {} });

  const recordParams = matchRoute({ to: '/app/o/$object/$recordId' });
  const indexParams = matchRoute({ to: '/app/o/$object' });
  const pageType: PageType = recordParams
    ? 'record-detail'
    : indexParams
      ? 'object-index'
      : matchRoute({ to: '/app/brief' })
        ? 'daily-brief'
        : matchRoute({ to: '/app/activity' })
          ? 'activity'
          : getPageHelp(path) ? 'help' : 'other';

  const routeObjectName = recordParams
    ? recordParams.object
    : indexParams
      ? indexParams.object
      : undefined;
  const object = schema.data?.objects.find(
    (candidate) => candidate.namePlural === routeObjectName || candidate.name === routeObjectName,
  );
  const pageSpecificContext = registeredContext.path === path ? registeredContext.value : {};
  const help = getPageHelp(path, pageSpecificContext);
  const viewType = pageSpecificContext.viewType;
  const view = object?.views.find((candidate) => candidate.type === viewType);
  const scopeLabel = recordParams
    ? `this ${object?.label ?? routeObjectName ?? 'record'}`
    : indexParams
      ? object?.labelPlural ?? routeObjectName ?? 'object'
      : pageType === 'daily-brief'
        ? 'Home'
        : help?.title ?? 'Activity';

  const setPageSpecificContext = useCallback((value: Record<string, unknown>) => {
    setRegisteredContext({ path, value });
  }, [path]);

  const pageContext = useMemo<PageContext>(() => ({
    path,
    route: path,
    ...(object ? { objectName: object.name } : {}),
    ...(recordParams ? { recordId: recordParams.recordId } : {}),
    ...(view ? { viewId: view.id } : {}),
    pageSpecificContext: {
      surface: 'web',
      pageType,
      ...pageSpecificContext,
      ...(help ? { pageTitle: help.title, helpExpertise: help.expertise } : {}),
    },
  }), [object, pageSpecificContext, pageType, path, recordParams, view, help?.title, help?.expertise]);

  const value = useMemo<PageContextValue>(() => ({
    pageContext,
    pageType,
    scopeLabel,
    setPageSpecificContext,
  }), [pageContext, pageType, scopeLabel, setPageSpecificContext]);

  return <CurrentPageContext.Provider value={value}>{children}</CurrentPageContext.Provider>;
}

export function useCurrentPageContext(): PageContextValue {
  const context = useContext(CurrentPageContext);
  if (!context) throw new Error('useCurrentPageContext must be used inside PageContextProvider.');
  return context;
}

/** Register only the relevant visible state, never credentials or whole API responses. */
export function usePageHelpDetails(details: Record<string, unknown>) {
  const { setPageSpecificContext } = useCurrentPageContext();
  const serialized = JSON.stringify(details);
  useEffect(() => {
    setPageSpecificContext(JSON.parse(serialized) as Record<string, unknown>);
    return () => setPageSpecificContext({});
  }, [serialized, setPageSpecificContext]);
}
